import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { http, API } from '@/lib/api';
import { CrisisChip, CrisisModal } from '@/components/CrisisBanner';
import { Send, History, Plus, Info } from 'lucide-react';
import { toast } from 'sonner';
import ContextualHelp from '@/components/ContextualHelp';
import CoachThreads from '@/components/CoachThreads';
import VoiceInputButton from '@/components/VoiceInputButton';
import LegalAckModal from '@/components/LegalAckModal';

const COACH_HELP = {
  whatIs: "Your AI Coach — a wise older brother, father figure, and mentor available 24/7. Warm, direct, and grounded in evidence-based frameworks like CBT and Motivational Interviewing.",
  whyMatters: "Reading and reflecting build awareness. Talking things through builds clarity. The Coach is where you take what you're learning and apply it to what's actually on your mind today.",
  howToUse: "Just start talking. Say what's really going on. The Coach will listen, reflect, and ask one focused question at a time. When you're ready for a plan, ask for one.",
  tips: [
    "The more context you give, the sharper the coaching. Don't stop at 'I'm stressed' — say what's under it.",
    "If a reply feels off, tell the Coach. It adjusts based on your feedback.",
    "The Coach isn't a therapist. For crisis or long-term care, use Find Therapy or call 988.",
  ],
};

// Health/medical keywords — when the conversation drifts into physical-health
// territory we surface a one-line "not medical advice" reminder (see below).
const HEALTH_RE = /\b(blood pressure|hypertension|medication|meds|prescription|prescribed|dosage|symptom|diagnos|cholesterol|diabet|blood sugar|heart|chest pain|dizzy|migraine|insomnia|sleep apnea|weight loss|obes|thyroid|kidney|liver|doctor|physician|clinic|hospital|blood test|lab result|std|sti|erectile)\b/i;

export default function Coach() {
  const { t } = useTranslation();
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [crisisOpen, setCrisisOpen] = useState(false);
  const bottomRef = useRef(null);
  const [search, setSearch] = useSearchParams();
  const [threadsOpen, setThreadsOpen] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const [trialLimit, setTrialLimit] = useState(null);

  const loadLimits = () => http.get('/coach/limits').then(r => setTrialLimit(r.data?.trial ? r.data : null)).catch(() => {});

  // Surface a medical disclaimer only when a health/medical topic comes up.
  const healthContext = useMemo(() => {
    const text = messages.map(m => m.content).join(' ') + ' ' + (topic || '');
    return HEALTH_RE.test(text);
  }, [messages, topic]);

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setTopic(null);
    setInput('');
  };

  const loadSession = async (sid) => {
    if (!sid) return;
    try {
      const r = await http.get(`/coach/sessions/${sid}`);
      const msgs = (r.data || []).map(m => ({ role: m.role, content: m.content }));
      setSessionId(sid);
      setMessages(msgs);
    } catch { toast.error("Couldn't load conversation"); }
  };

  useEffect(() => { http.get('/coach/topics').then(r => setTopics(r.data)); }, []);
  useEffect(() => { loadLimits(); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Auto-ask from ?prompt= (e.g. a lesson's AI Coach prompt) — send it, don't just pre-fill.
  useEffect(() => {
    const q = search.get('prompt');
    if (q) {
      setPendingPrompt(q);
      search.delete('prompt');
      setSearch(search, { replace: true });
    }
  }, [search, setSearch]);

  // Pre-fill from sessionStorage (used by Watch & Learn "Discuss with AI Coach").
  useEffect(() => {
    try {
      const seed = sessionStorage.getItem('coach_starter_message');
      if (seed) {
        setInput(seed);
        sessionStorage.removeItem('coach_starter_message');
      }
    } catch { /* no-op */ }
  }, []);

  const send = async (explicit) => {
    const text = (typeof explicit === 'string' ? explicit : input).trim();
    if (!text || streaming) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/coach/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, topic, message: text }),
      });
      if (!res.ok) {
        const err = await res.json().catch(()=>({detail: 'Something went wrong'}));
        setMessages(m => { const copy = [...m]; copy[copy.length-1] = { role: 'assistant', content: err.detail || 'Error' }; return copy; });
        if (res.status === 402) {
          if (res.headers.get('X-Trial-Cap')) {
            toast.error(err.detail, { duration: 8000, action: { label: 'Upgrade', onClick: () => { window.location.href = '/subscription/checkout'; } } });
          } else {
            toast.error(err.detail);
          }
        }
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const chunk = line.slice(6);
          if (chunk === '[DONE]') { setStreaming(false); continue; }
          const sessionMatch = chunk.match(/^\[SESSION:(.+)\]$/);
          if (sessionMatch) { if (!sessionId) setSessionId(sessionMatch[1]); continue; }
          const nudgeMatch = chunk.match(/^\[NUDGE:(\d+)\]$/);
          if (nudgeMatch) { const n = nudgeMatch[1]; toast(`${n} more Coach message${n === '1' ? '' : 's'} today — we can go a bit more.`); continue; }
          const decoded = chunk.replace(/\\n/g, '\n');
          setMessages(m => {
            const copy = [...m];
            copy[copy.length-1] = { role: 'assistant', content: copy[copy.length-1].content + decoded };
            return copy;
          });
        }
      }
      if (!sessionId) {
        // Fetch latest session (simplest: leave undefined so next msg creates new)
      }
    } catch (e) {
      toast.error('Chat failed');
    } finally { setStreaming(false); if (trialLimit) loadLimits(); }
  };

  // Fire the pending lesson prompt once the coach is ready (auto-ask, not just pre-fill).
  useEffect(() => {
    if (pendingPrompt && !streaming) {
      const q = pendingPrompt;
      setPendingPrompt(null);
      send(q);
    }
  }, [pendingPrompt]);

  return (
    <div className="flex flex-col min-h-[calc(100vh-6rem)] lg:min-h-[calc(100vh-4rem)] lg:max-w-4xl lg:mx-auto lg:w-full" data-testid="coach-page">
      <LegalAckModal
        storageKey="bp_ack_coach_v1"
        title="Before you chat with the AI Coach"
        body="Brotherhood+ AI Coach is designed for education, wellness, and personal growth. It is not a substitute for professional mental health care."
        readMoreTo="/app/profile/trust-safety?section=ai-disclosure"
        readMoreLabel="Read the AI Coach Disclosure"
      />
      <div className="px-6 lg:px-2 pt-6 lg:pt-10 pb-3 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">{t('coach.title')}</p>
          <h1 className="font-heading text-2xl lg:text-4xl mt-1 lg:mt-2 flex items-start gap-2 flex-wrap">
            {t('coach.starter_help')}
            <ContextualHelp pageTitle="AI Coach" content={COACH_HELP} testid="coach-help"/>
          </h1>
          <p className="hidden lg:block text-navy/60 mt-2">{t('coach.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <button
              onClick={startNewChat}
              data-testid="coach-new-chat"
              aria-label="New conversation"
              className="hidden lg:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium bg-beige/70 hover:bg-beige text-navy transition-smooth"
            >
              <Plus size={13} strokeWidth={2}/> New
            </button>
          )}
          <button
            onClick={() => setThreadsOpen(true)}
            data-testid="coach-threads-open"
            aria-label="Your saved conversations"
            title="Your saved conversations"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium bg-beige/70 hover:bg-beige text-navy transition-smooth"
          >
            <History size={14} strokeWidth={2}/> <span className="hidden sm:inline">Saved chats</span>
          </button>
          <CrisisChip onOpen={()=>setCrisisOpen(true)} />
        </div>
      </div>

      {messages.length === 0 && (
        <div className="px-6 lg:px-2 pb-3">
          <div className="flex flex-wrap gap-2">
            {topics.map(t => (
              <button key={t} data-testid={`topic-${t}`} onClick={()=>{ setTopic(t); setInput(`I want to talk about ${t.toLowerCase()}.`); }}
                className={`text-xs lg:text-sm px-4 py-2 lg:px-5 lg:py-2.5 rounded-full transition-smooth ${topic===t ? 'bg-navy text-white' : 'bg-beige text-navy hover:bg-[#EAE5D9]'}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-6 lg:px-2 py-4 space-y-3 lg:space-y-4">
        {messages.length === 0 && (
          <div className="text-sm lg:text-base text-navy/60 bg-white rounded-3xl p-6 lg:p-8 card-soft lg:max-w-2xl">
            {t('coach.empty_state')}
          </div>
        )}
        {messages.map((m,i) => (
          <div key={i} className={`flex ${m.role==='user' ? 'justify-end' : 'justify-start'} items-end gap-2`}>
            <div data-testid={`msg-${m.role}-${i}`} className={`max-w-[85%] lg:max-w-[75%] rounded-3xl px-5 py-3 lg:px-6 lg:py-4 whitespace-pre-wrap text-sm lg:text-base leading-relaxed ${
              m.role==='user' ? 'bg-navy text-white' : 'bg-white text-navy card-soft'
            }`}>{m.content || (streaming && i===messages.length-1 ? '…' : '')}</div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="px-6 lg:px-2 pb-6 lg:pb-8 pt-2 bg-[#F9F8F6]">
        {healthContext && (
          <div data-testid="coach-medical-note" className="mb-2 flex items-start gap-2 rounded-xl border border-gold/30 bg-gold/[0.06] px-3 py-2">
            <Info size={13} className="text-gold shrink-0 mt-0.5"/>
            <p className="text-[11px] lg:text-xs text-navy/70 leading-relaxed">
              This is general education, <span className="font-semibold text-navy">not medical advice</span>. For anything about your health — blood pressure, medications, or symptoms — talk to a licensed doctor.
            </p>
          </div>
        )}
        {trialLimit && (() => {
          const rem = trialLimit.ai_remaining ?? 0;
          const label = `${rem} AI use${rem === 1 ? '' : 's'} left in your free trial`;
          const low = rem <= 2;
          return (
            <div data-testid="coach-trial-hint"
              className={`mb-2 flex items-center justify-center gap-2 rounded-full px-3 py-1.5 text-[11px] lg:text-xs ${low ? 'bg-gold/15 text-navy' : 'bg-navy/5 text-navy/60'}`}>
              <span className="font-medium" data-testid="coach-trial-remaining">{label}</span>
              {rem === 0 && (
                <a href="/subscription/checkout" className="underline font-semibold text-gold" data-testid="coach-trial-upgrade">Upgrade for unlimited</a>
              )}
            </div>
          );
        })()}
        <p className="text-[10px] lg:text-xs text-navy/40 mb-2 text-center">Not therapy. Not a diagnosis. If in crisis, call or text 988.</p>
        <div className="flex gap-2 items-end bg-white rounded-3xl p-2 lg:p-3 card-soft">
          <textarea
            data-testid="coach-input"
            value={input}
            onChange={(e)=>setInput(e.target.value)}
            onKeyDown={(e)=>{ if(e.key==='Enter'&&!e.shiftKey){ e.preventDefault(); send(); } }}
            rows={1}
            placeholder={t('coach.input_placeholder')}
            className="flex-1 resize-none px-4 py-3 bg-transparent outline-none text-sm lg:text-base max-h-40"
          />
          <VoiceInputButton
            testid="coach-voice-btn"
            onTranscript={(chunk) => setInput(prev => (prev + ' ' + chunk).trim())}
          />
          <button data-testid="coach-send" onClick={send} disabled={streaming || !input.trim()}
            className="w-11 h-11 lg:w-12 lg:h-12 rounded-full bg-navy text-white flex items-center justify-center disabled:opacity-40 hover:bg-navy-soft transition-smooth">
            <Send size={16}/>
          </button>
        </div>
      </div>

      <CrisisModal open={crisisOpen} onClose={()=>setCrisisOpen(false)} />
      <CoachThreads
        open={threadsOpen}
        onClose={() => setThreadsOpen(false)}
        currentSessionId={sessionId}
        onSelect={loadSession}
        onNewChat={startNewChat}
      />
    </div>
  );
}
